import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const applicationsData = [
  {
    title: "Modular Kitchens",
    description: "High-gloss acrylic boards engineered for scratch resistance and moisture stability in modular cabinetry systems.",
    image: "/modular-kitchen-app.png",
    alt: "Acrylic laminated boards for modular kitchen"
  },
  {
    title: "Wardrobes",
    description: "Durable laminated panels and doors designed for long-term structural integrity in bedroom storage systems.",
    image: "/wardrobe-application-new.png",
    alt: "Premium laminated wardrobe panels"
  },
  {
    title: "Commercial Interiors",
    description: "Performance-driven surfaces ideal for offices, retail outlets, and institutional spaces.",
    image: "/commercial-interiors-app.png",
    alt: "Commercial interior laminate solutions"
  },
  {
    title: "Residential Doors",
    description: "Precision-manufactured laminated doors suitable for all interior applications.",
    image: "/residential-doors-app.png",
    alt: "Residential laminated doors"
  },
  {
    title: "Wall Paneling",
    description: "Decorative wall panels engineered for consistent finish and easy installation.",
    image: "/wall-paneling-app.png",
    alt: "Decorative wall panel systems"
  },
  {
    title: "Hospitality & Hotels",
    description: "High-durability finishes built for heavy footfall and high-use environments.",
    image: "/hotel-hospitality-app.png",
    alt: "Hospitality laminate solutions for hotels"
  }
];

const Applications = () => {
  return (
    <div className="min-h-screen bg-[#F7F8F5]">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="label-mono text-[#D4AF37] mb-4">Where We Excel</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Applications
            </h1>
            <p className="text-xl text-white/70 max-w-2xl">
              Designed for durability across high-traffic and high-performance environments. Our engineered surfaces transform residential and commercial spaces with long-term performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {applicationsData.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`relative overflow-hidden group ${
                  index === 0 ? 'lg:row-span-2 h-80 lg:h-full' : 'h-80'
                }`}
              >
                {/* Image */}
                <img
                  src={app.image}
                  alt={app.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#004534]/90 via-[#004534]/50 to-transparent" />
                
                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8">
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2 font-['Outfit']">
                    {app.title}
                  </h3>
                  <p className="text-white/80 text-sm lg:text-base mb-4">
                    {app.description}
                  </p>
                  <div className="flex items-center text-[#D4AF37] font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore Products
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#004534] relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#D4AF37]/10 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.p 
              className="label-mono text-[#D4AF37] mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Partner With Us
            </motion.p>
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Build with Materials That Perform
            </motion.h2>
            <motion.p 
              className="text-lg text-white/70 mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Connect with our team for specifications, technical consultation, or dealer partnership inquiries
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/contact">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors flex items-center gap-2">
                  Request Technical Consultation
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/dealer">
                <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">
                  Become an Authorized Dealer
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Applications;
